import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Popper, Paper, ClickAwayListener, Select, MenuItem } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import moment from 'moment-timezone';

const EST_TIMEZONE = 'America/New_York';

type CDDateTimePickerProps = {
    value: Date | null;
    onChange: (newValue: Date | null) => void;
    showError?: boolean;
    disabled?: boolean;
    label: string;
    disableFuture?: boolean;
    disablePast?: boolean;
    minDate?: Date;
    maxDate?: Date;
    required?: boolean;
    sx?: object;
};

function getHour12(date: Date): number {
    const h = moment.tz(date, EST_TIMEZONE).hour();
    const h12 = h % 12;
    return h12 === 0 ? 12 : h12;
}

function getMinute(date: Date): number {
    return moment.tz(date, EST_TIMEZONE).minute();
}

function getAmPm(date: Date): 'AM' | 'PM' {
    return moment.tz(date, EST_TIMEZONE).hour() >= 12 ? 'PM' : 'AM';
}

export default function CDDateTimePicker(props: CDDateTimePickerProps) {
    const { value, onChange, showError, disabled, label, disableFuture, disablePast, minDate, maxDate, sx } = props;

    const [open, setOpen] = useState(false);
    const [hour, setHour] = useState<number>(12);
    const [minute, setMinute] = useState<number>(0);
    const [ampm, setAmpm] = useState<'AM' | 'PM'>('AM');
    const anchorRef = useRef<HTMLDivElement>(null);
    const selectOpenRef = useRef(false);

    useEffect(() => {
        if (value) {
            setHour(getHour12(value));
            setMinute(getMinute(value));
            setAmpm(getAmPm(value));
        }
    }, [value]);

    const applyTimeToDate = (date: Date, h: number, m: number, period: 'AM' | 'PM'): Date => {
        let hour24 = h % 12;
        if (period === 'PM') hour24 += 12;
        const est = moment.tz(date, EST_TIMEZONE);
        est.set({ hour: hour24, minute: m, second: 0, millisecond: 0 });
        return est.toDate();
    };

    const handleDateChange = (newDate: Date | null) => {
        if (!newDate) {
            onChange(null);
            return;
        }
        const year = newDate.getFullYear();
        if (year < 2000) {
            newDate.setFullYear(2000 + (year % 100));
        }
        const result = applyTimeToDate(newDate, hour, minute, ampm);
        onChange(result);
    };

    const handleTimeChange = (newHour: number, newMinute: number, newAmPm: 'AM' | 'PM') => {
        setHour(newHour);
        setMinute(newMinute);
        setAmpm(newAmPm);
        if (value) {
            const result = applyTimeToDate(value, newHour, newMinute, newAmPm);
            onChange(result);
        }
    };

    const handleOpen = () => {
        if (!disabled) setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 60 }, (_, i) => i);

    const hasError = showError && !value;
    const dateLine = value ? moment.tz(value, EST_TIMEZONE).format('M/D/YY') : '';
    const timeLine = value ? moment.tz(value, EST_TIMEZONE).format('h:mm A') : '';

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
                ref={anchorRef}
                onClick={handleOpen}
                sx={{
                    border: '1px solid',
                    borderColor: hasError ? 'error.main' : 'divider',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    minWidth: 80,
                    maxWidth: 100,
                    '&:hover': disabled
                        ? {}
                        : {
                              borderColor: 'text.primary',
                          },
                    ...sx,
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.625rem', lineHeight: 1.2, display: 'block' }}
                >
                    {label}
                </Typography>
                {value ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Box>
                            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.3, fontWeight: 500 }}>
                                {dateLine}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, color: 'text.secondary' }}>
                                {timeLine} ET
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', lineHeight: 1.3, mt: 0.25 }}>
                        Select
                    </Typography>
                )}
            </Box>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
                modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            >
                <ClickAwayListener
                    onClickAway={() => {
                        if (selectOpenRef.current) return;
                        handleClose();
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            width: 320,
                        }}
                    >
                        <DateCalendar
                            value={value}
                            onChange={handleDateChange}
                            disableFuture={disableFuture}
                            disablePast={disablePast}
                            minDate={minDate}
                            maxDate={maxDate}
                            slots={{
                                leftArrowIcon: () => <ChevronLeftIcon />,
                                rightArrowIcon: () => <ChevronRightIcon />,
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiPickersCalendarHeader-root': {
                                    pl: 2,
                                    pr: 1,
                                },
                                '& .MuiPickersDay-root.Mui-selected': {
                                    backgroundColor: '#2e4a6e',
                                    '&:hover': {
                                        backgroundColor: '#1e3a5e',
                                    },
                                },
                                '& .MuiPickersDay-root.MuiPickersDay-today': {
                                    border: '1px solid #2e4a6e',
                                },
                            }}
                        />

                        <Box sx={{ px: 3, pb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                America/New_York
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    gap: 0.5,
                                }}
                            >
                                <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />

                                <Select
                                    value={hour}
                                    onChange={(e) => handleTimeChange(Number(e.target.value), minute, ampm)}
                                    onOpen={() => {
                                        selectOpenRef.current = true;
                                    }}
                                    onClose={() => {
                                        selectOpenRef.current = false;
                                    }}
                                    variant="standard"
                                    disableUnderline
                                    sx={{
                                        minWidth: 48,
                                        '& .MuiSelect-select': {
                                            py: 0,
                                            textAlign: 'center',
                                            fontSize: '0.95rem',
                                        },
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                                >
                                    {hours.map((h) => (
                                        <MenuItem key={h} value={h}>
                                            {String(h).padStart(2, '0')}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <Typography sx={{ mx: 0.25, fontSize: '0.95rem' }}>:</Typography>

                                <Select
                                    value={minute}
                                    onChange={(e) => handleTimeChange(hour, Number(e.target.value), ampm)}
                                    onOpen={() => {
                                        selectOpenRef.current = true;
                                    }}
                                    onClose={() => {
                                        selectOpenRef.current = false;
                                    }}
                                    variant="standard"
                                    disableUnderline
                                    sx={{
                                        minWidth: 48,
                                        '& .MuiSelect-select': {
                                            py: 0,
                                            textAlign: 'center',
                                            fontSize: '0.95rem',
                                        },
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                                >
                                    {minutes.map((m) => (
                                        <MenuItem key={m} value={m}>
                                            {String(m).padStart(2, '0')}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <Select
                                    value={ampm}
                                    onChange={(e) => handleTimeChange(hour, minute, e.target.value as 'AM' | 'PM')}
                                    onOpen={() => {
                                        selectOpenRef.current = true;
                                    }}
                                    onClose={() => {
                                        selectOpenRef.current = false;
                                    }}
                                    variant="standard"
                                    disableUnderline
                                    sx={{
                                        minWidth: 48,
                                        ml: 0.5,
                                        '& .MuiSelect-select': {
                                            py: 0,
                                            textAlign: 'center',
                                            fontSize: '0.95rem',
                                        },
                                    }}
                                >
                                    <MenuItem value="AM">AM</MenuItem>
                                    <MenuItem value="PM">PM</MenuItem>
                                </Select>
                            </Box>
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </LocalizationProvider>
    );
}
